export type PartnerReportViewDeviceType = "desktop" | "mobile" | "tablet" | "unknown";

/** A signed-out view that came from a browser or network an admin also used. */
export type PartnerReportViewAdminMatch = "browser" | "network";

export type PartnerReportViewEntry = {
  id: string;
  viewedAt: string;
  signedIn: boolean;
  viewerLabel: string | null;
  deviceType: PartnerReportViewDeviceType;
  browser: string | null;
  os: string | null;
  location: string | null;
  isBot: boolean;
  botName: string | null;
  isAdmin: boolean;
  ipAddress: string | null;
  /** Short label for the browser cookie, so repeat opens from one browser line up. */
  browserTag: string | null;
  adminMatch: PartnerReportViewAdminMatch | null;
};

export type PartnerReportViewStats = {
  /** Opens by real prospects: bots, admins, and admin browsers or networks are left out. */
  views: number;
  firstOpenedAt: string | null;
  lastOpenedAt: string | null;
  /** Newest first, every kind of view, flagged so the UI can label it. */
  recentViews: PartnerReportViewEntry[];
};

export type PartnerReportViewFlags = {
  viewedAt: Date | string;
  isBot: boolean;
  isAdmin: boolean;
  adminMatch?: PartnerReportViewAdminMatch | null;
};

export const PARTNER_REPORT_RECENT_VIEWS_LIMIT = 8;

export const EMPTY_PARTNER_REPORT_VIEW_STATS: PartnerReportViewStats = {
  views: 0,
  firstOpenedAt: null,
  lastOpenedAt: null,
  recentViews: [],
};

export function isCountedPartnerReportView(view: {
  isBot: boolean;
  isAdmin: boolean;
  adminMatch?: PartnerReportViewAdminMatch | null;
}): boolean {
  return !view.isBot && !view.isAdmin && !view.adminMatch;
}

/** Count prospect opens and find the first and last one. Order of `views` does not matter. */
export function summarizePartnerReportViewCounts(
  views: readonly PartnerReportViewFlags[],
): Pick<PartnerReportViewStats, "views" | "firstOpenedAt" | "lastOpenedAt"> {
  let count = 0;
  let first: number | null = null;
  let last: number | null = null;
  for (const view of views) {
    if (!isCountedPartnerReportView(view)) continue;
    const at = new Date(view.viewedAt).getTime();
    if (Number.isNaN(at)) continue;
    count += 1;
    if (first === null || at < first) first = at;
    if (last === null || at > last) last = at;
  }
  return {
    views: count,
    firstOpenedAt: first === null ? null : new Date(first).toISOString(),
    lastOpenedAt: last === null ? null : new Date(last).toISOString(),
  };
}

/** Counts over every view, plus the newest few for the admin list. */
export function buildPartnerReportViewStats(
  views: readonly PartnerReportViewEntry[],
  recentLimit: number = PARTNER_REPORT_RECENT_VIEWS_LIMIT,
): PartnerReportViewStats {
  const newestFirst = [...views].sort(
    (a, b) => new Date(b.viewedAt).getTime() - new Date(a.viewedAt).getTime(),
  );
  return {
    ...summarizePartnerReportViewCounts(views),
    recentViews: newestFirst.slice(0, Math.max(0, recentLimit)),
  };
}

export const REPORT_VIEWED_EVENT = "report_viewed";

export function buildReportViewedEventProperties(input: {
  token: string;
  reportId: string;
  partyId: string;
  companyName: string;
  signedIn: boolean;
  isAdmin: boolean;
  deviceType: PartnerReportViewDeviceType;
  country: string | null;
}): Record<string, string | boolean | null> {
  return {
    token: input.token,
    report_id: input.reportId,
    party_id: input.partyId,
    company_name: input.companyName,
    signed_in: input.signedIn,
    is_admin: input.isAdmin,
    device_type: input.deviceType,
    country: input.country,
  };
}

export function formatPartnerReportViewLocation(geo: {
  city: string | null;
  region: string | null;
  country: string | null;
}): string | null {
  const parts = [geo.city, geo.region, geo.country]
    .map((part) => part?.trim() ?? "")
    .filter((part) => part.length > 0);
  return parts.length > 0 ? parts.join(", ") : null;
}

const IPV4_PATTERN = /^(?:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;
const IPV6_GROUP_PATTERN = /^[0-9a-f]{1,4}$/;

function expandIpv6(value: string): string[] | null {
  const halves = value.split("::");
  if (halves.length > 2) return null;
  const head = halves[0] ? halves[0].split(":") : [];
  const tail = halves.length === 2 && halves[1] ? halves[1].split(":") : [];
  const missing = 8 - head.length - tail.length;
  if (halves.length === 2 ? missing < 1 : missing !== 0) return null;
  const groups = [...head, ...Array<string>(Math.max(0, missing)).fill("0"), ...tail];
  if (!groups.every((group) => IPV6_GROUP_PATTERN.test(group))) return null;
  return groups.map((group) => group.replace(/^0+(?=.)/, ""));
}

/**
 * The network a viewer was on. IPv4 keeps the full address. IPv6 keeps the /64
 * prefix, because one home or office network rotates the last 64 bits.
 */
export function partnerReportNetworkKey(ip: string | null | undefined): string | null {
  const value = ip?.trim().toLowerCase() ?? "";
  if (!value) return null;
  if (IPV4_PATTERN.test(value)) return value;
  const mapped = /^::ffff:(\d+\.\d+\.\d+\.\d+)$/.exec(value)?.[1];
  if (mapped && IPV4_PATTERN.test(mapped)) return mapped;
  const groups = expandIpv6(value);
  if (!groups) return null;
  return `${groups.slice(0, 4).join(":")}::/64`;
}

export type PartnerReportAdminFingerprints = {
  visitorIds: readonly string[];
  networkKeys: readonly string[];
};

export function matchPartnerReportViewToAdmin(
  view: { visitorId: string | null; ipAddress: string | null },
  admin: PartnerReportAdminFingerprints,
): PartnerReportViewAdminMatch | null {
  if (view.visitorId && admin.visitorIds.includes(view.visitorId)) return "browser";
  const network = partnerReportNetworkKey(view.ipAddress);
  if (network && admin.networkKeys.includes(network)) return "network";
  return null;
}

export function partnerReportBrowserTag(visitorId: string | null | undefined): string | null {
  const compact = visitorId?.replace(/[^0-9a-z]/gi, "") ?? "";
  return compact ? compact.slice(0, 6).toUpperCase() : null;
}
